import { Component, OnDestroy, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { RESPONSIVE_BREAKPOINTS } from 'src/app/extensions/constants/responsive-breakpoints';
import { WindowEventsService } from 'src/app/extensions/services/window-events.services';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from 'src/app/core/services/notification.service';
import { EmailsListFilterComponent } from './emails-list-filter/emails-list-filter.component';
import { EmailAPIService } from 'src/app/extensions/services/api/email-api.service';
import { EmailsAddEditComponent } from './emails-add-edit/emails-add-edit.component';
import { isNotEmpty, setTableViewMode } from 'src/app/extensions/utilities';
import { NO_RECORDS_IMAGE_SRC, TABLE_VIEW_MODES } from 'src/app/extensions/constants/commonConstants';
import { GlobalService } from 'src/app/extensions/services/global.service';
import { Router } from '@angular/router';
import { DeleteItemModalComponent } from 'src/app/extensions/components/delete-item-modal/delete-item-modal.component';

@Component({
  selector: 'app-emails-list',
  templateUrl: './emails-list.component.html',
  styleUrls: ['./emails-list.component.scss'],
  standalone: false,
})
export class EmailsListComponent implements OnInit, OnDestroy {
  public emails: app.models.Email[];
  public props: app.models.DynamicPropsDictionary;
  public activeFilterCount = 0;

  public currentRoute: string;
  public TABLE_VIEW_MODES = TABLE_VIEW_MODES;
  public tableViewMode: string;
  windowSizeLgUp: boolean;
  public subscriptions: Subscription[] = [];

  public hasData = true;
  public hasFilterData = true;
  public isLoadedData = false;

  NO_RECORDS_IMAGE_SRC = NO_RECORDS_IMAGE_SRC;

  public pageFilter: app.models.EmailXRequestTypes;
  public initialFilterState: app.models.EmailXRequestTypes;

  public constructor(
    private _globalService: GlobalService,
    private _router: Router,
    private _emailAPIService: EmailAPIService,
    private _ngbModal: NgbModal,
    private _windowEventService: WindowEventsService,
    private _translateService: TranslateService,
    private _notificationService: NotificationService
  ) {
    this.currentRoute = this._router.url.split('/')[1];
  }

  public ngOnInit(): void {
    this.tableViewMode = setTableViewMode(this._globalService, this.currentRoute, TABLE_VIEW_MODES.CARDS_VIEW);

    this.pageFilter = {
      emailAddress: '',
      emailId: '',
      dictionaryAssetDocumentRequestTypeIds: [],
      dictionaryClientDataRequestTypeIds: [],
      dictionaryContractModificationRequestTypeIds: [],
    } as app.models.EmailXRequestTypes;
    this.initialFilterState = structuredClone(this.pageFilter);
    this.activeFilterCount = 0;
    this.hasFilterData = true;

    this.loadData();
    this.windowSizeLgUp = window.innerWidth >= RESPONSIVE_BREAKPOINTS.lg;
    let subscription = this._windowEventService.onResize.subscribe(() => {
      this.windowSizeLgUp = window.innerWidth >= RESPONSIVE_BREAKPOINTS.lg;
    });
    this.subscriptions.push(subscription);
  }

  loadData() {
    this._emailAPIService.getEmails(this.pageFilter).subscribe(result => {
      this.emails = result.data;
      this.hasData = this.emails.length != 0;
      this.props = result.props;
      this.isLoadedData = true;
    });
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.forEach(subscription => {
        subscription.unsubscribe();
      });
    }
  }

  public filterHandler() {
    let instance = this._ngbModal.open(EmailsListFilterComponent, { scrollable: true });
    instance.componentInstance.pageFilter = this.pageFilter;
    instance.componentInstance.initialFilterState = this.initialFilterState;

    instance.closed.subscribe(result => {
      if (result !== false) {
        this.initialFilterState = structuredClone(this.pageFilter);
        this.activeFilterCount = result;
        this._emailAPIService.getEmails(this.pageFilter).subscribe(x => {
          this.emails = x.data;
          this.hasFilterData = this.emails.length != 0;
          this.props = x.props;
        });
      }
    });
  }

  public resetFiltersHandler() {
    this.pageFilter.dictionaryAssetDocumentRequestTypeIds = [];
    this.pageFilter.dictionaryContractModificationRequestTypeIds = [];
    this.pageFilter.dictionaryClientDataRequestTypeIds = [];
    this.hasFilterData = true;
    this.activeFilterCount = 0;
    this.loadData();
  }

  isActiveFilterData(): boolean {
    return (
      this.pageFilter.dictionaryAssetDocumentRequestTypeIds.length > 0 ||
      this.pageFilter.dictionaryContractModificationRequestTypeIds.length > 0 ||
      this.pageFilter.dictionaryClientDataRequestTypeIds.length > 0
    );
  }

  navigateAddFormHandler() {
    this.viewDetailsHandler();
  }

  viewDetailsHandler = (emailId: string = '') => {
    const addEditEmailModal = this._ngbModal.open(EmailsAddEditComponent);
    addEditEmailModal.componentInstance.emailId = emailId;
    addEditEmailModal.result.then(result => {
      if (result) {
        if (isNotEmpty(emailId)) {
          let email = this.emails.find(x => x.emailId === emailId);
          email.emailAddress = result.emailAddress;
        } else {
          this.emails.push({
            emailId: result.emailId,
            emailAddress: result.emailAddress,
          } as app.models.Email);
          this.hasData = true;
        }
      }
    });
  };

  public deleteEmailHandler = (emailId: string = null) => {
    const deleteEmailModal = this._ngbModal.open(DeleteItemModalComponent, { backdrop: 'static' });
    var email = this.emails.find(x => x.emailId == emailId);

    deleteEmailModal.componentInstance.title = 'email.deleteEmailTitle';
    deleteEmailModal.componentInstance.item = email.emailAddress;
    deleteEmailModal.componentInstance.warninglabel = this._translateService.instant('email.deleteEmailWarningAll');
    deleteEmailModal.componentInstance.values = [];

    deleteEmailModal.result.then(result => {
      if (result) {
        this._emailAPIService.deleteEmail(emailId).subscribe(statusChangeResponse => {
          if (statusChangeResponse.isSuccess) {
            this.emails = this.emails.filter(x => x.emailId != emailId);
            if (this.emails.length === 0) {
              if (this.isActiveFilterData()) {
                this.hasFilterData = false;
              } else {
                this.hasData = false;
              }
            }
            this._notificationService.showSuccess(
              this._translateService.instant('email.emailDeleted'),
              this._translateService.instant('common.success')
            );
          }
        });
      }
    });
  };
}
